import React, { useState } from "react";
import { get, isEmpty } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Input, Form, Button, Message, Segment } from "semantic-ui-react";

import {
    changeInvoiceBuilderStamp,
    addInvoiceBuilderStampRoll,
    openInvoiceBuilderStampForm,
    getInvoiceStampsInterval,
    cancelInvoiceBuilderStampForm,
} from "../builder/actions";
import { formatStampRange } from "../../common/helpers";
import { countStampsNeeded, computeMissingStamps } from "../helpers";

const clearStampInterval = () => ({
    roll_id: undefined,
    prefix: undefined,
    roll_from: undefined,
    roll_to: undefined,
    first_available: undefined,
    stamp_contiguous_end: undefined,
    stamps_needed: undefined,
    stamps_assignable: undefined,
    from: "",
    to: "",
});

const InvoiceBuilderContainerStampInput = () => {
    const { stamps, availableStamps, editingStamp, dsCount } = useSelector(
        ({ invoiceBuilder }) => ({
            stamps: invoiceBuilder.stamps,
            availableStamps: invoiceBuilder.availableStamps,
            editingStamp: invoiceBuilder.editingStamp,
            dsCount: countStampsNeeded({ items: invoiceBuilder.items }),
        })
    );

    const dispatch = useDispatch();
    const [stampStartError, setStampStartError] = useState("");
    const [stampEndError, setStampEndError] = useState("");

    const [isResolving, setIsResolving] = useState(false);
    const rollExists = editingStamp.roll_id != null;

    const handleStampChange = editingStamp => {
        if (editingStamp.from !== undefined) setStampStartError("");
        if (editingStamp.to !== undefined) setStampEndError("");
        if (!editingStamp.from) {
            editingStamp.to = "";
            editingStamp.roll_id = undefined;
        }
        dispatch(changeInvoiceBuilderStamp(editingStamp));
    };

    const handleStampRollAdd = e => {
        e.preventDefault();
        const currentStart = Number(editingStamp.from);
        const currentEnd = Number(editingStamp.to);
        const currentStampCount =
            Number.isInteger(currentStart) &&
            Number.isInteger(currentEnd) &&
            currentEnd >= currentStart
                ? currentEnd - currentStart + 1
                : 0;
        const remainingStampsNeeded = Math.max(
            computeMissingStamps(dsCount, stamps) - currentStampCount,
            0
        );
        dispatch(addInvoiceBuilderStampRoll());
        if (remainingStampsNeeded > 0) {
            dispatch(openInvoiceBuilderStampForm());
        }
    };

    const handleInputClear = () => {
        setStampStartError("");
        setStampEndError("");
        dispatch(cancelInvoiceBuilderStampForm());
    };

    const resolveStampStart = async () => {
        const startNumber = Number(editingStamp.from);
        if (!startNumber) {
            setStampStartError("Enter a valid stamp start number.");
            return;
        }
        if (isResolving) return;
        setIsResolving(true);
        try {
            const reclaimableIntervals = availableStamps.map(stamp => ({
                from: stamp.from,
                to: stamp.to,
                roll_id: stamp.roll_id,
            }));

            const reservedIntervals = stamps.map(stamp => ({
                from: stamp.from,
                to: stamp.to,
                roll_id: stamp.roll_id,
            }));

            const missingStamps = Math.max(computeMissingStamps(dsCount, stamps), 0);
            editingStamp.to = "";

            const stampData = await dispatch(
                getInvoiceStampsInterval({
                    from: startNumber,
                    dsCount: missingStamps,
                    reclaimableIntervals,
                    reservedIntervals,
                })
            );
            setStampStartError("");
            validateStampEnd(stampData);
            checkOverlap(stampData);
        } catch (err) {
            setStampStartError(get(err, "response.data.message", null) || err.message);
            dispatch(changeInvoiceBuilderStamp(clearStampInterval()));
        } finally {
            setIsResolving(false);
        }
    };

    const addStamps = () => {
        dispatch(openInvoiceBuilderStampForm());
    };

    const validateStampEnd = (next = editingStamp) => {
        const end = next.to;
        const start = next.from;
        const contiguousEnd = next.stamp_contiguous_end;
        setStampEndError("");
        if (end !== "" && Number(end) < Number(start)) {
            setStampEndError(
                `End stamp must be greater than or equal to the start stamp number ${start}.`
            );
        }
        if (end !== "" && Number(end) > Number(contiguousEnd)) {
            setStampEndError(
                `End stamp ${end} is greater than the contiguous range ending at ${contiguousEnd}.`
            );
        }
        if (!end) {
            setStampEndError(`End stamp value should be set.`);
        }
    };

    const checkOverlap = currentStamp => {
        const currentStart = Number(currentStamp.from);
        const currentEnd = currentStamp.to ? Number(currentStamp.to) : undefined;

        stamps.forEach(stamp => {
            const existingStart = Number(stamp.from);
            const existingEnd = Number(stamp.to);

            if (
                currentStamp.roll_id !== stamp.roll_id ||
                existingStart == null ||
                existingEnd == null
            )
                return;

            const hasOverlap =
                (currentStart >= existingStart && currentStart <= existingEnd) ||
                (currentEnd <= existingEnd && currentEnd >= existingStart) ||
                (currentStart <= existingStart && currentEnd >= existingEnd);

            if (hasOverlap) {
                setStampStartError(
                    `${formatStampRange({
                        from: currentStart,
                        to: currentEnd,
                        prefix: currentStamp.prefix || "",
                    })} overlaps ${formatStampRange({
                        from: existingStart,
                        to: existingEnd,
                        prefix: stamp.prefix || "",
                    })}`
                );
            }
        });
    };

    if (isEmpty(editingStamp)) {
        return (
            <Button
                onClick={addStamps}
                attached="bottom"
                content="Add Stamps"
                size="tiny"
                icon="plus"
                data-cy="add_stamps_button"
            />
        );
    }

    return (
        <>
            <Segment attached>
                <Form>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <label data-cy="stamp_start_label">Stamp Start</label>
                            <Input
                                size="small"
                                type="number"
                                value={editingStamp.from}
                                placeholder="Stamps From"
                                className="hide-number-arrows"
                                loading={isResolving}
                                data-cy="stamps_from"
                                icon={{
                                    name: "close",
                                    link: true,
                                    onClick: e => {
                                        e.preventDefault();
                                        handleStampChange(clearStampInterval());
                                    },
                                }}
                                onChange={(e, { value }) => {
                                    const next = { ...editingStamp, from: value };
                                    next.to = "";
                                    handleStampChange(next);
                                    validateStampEnd(next);
                                }}
                                onKeyPress={async e => {
                                    if (e.key !== "Enter") return;
                                    e.preventDefault();
                                    await resolveStampStart();
                                }}
                                onBlur={async e => {
                                    e.preventDefault();
                                    await resolveStampStart();
                                }}
                            />
                            {!!stampStartError && (
                                <div
                                    data-cy="stamp_start_error"
                                    className="ui pointing above prompt label"
                                >
                                    {stampStartError}
                                </div>
                            )}
                        </Form.Field>
                        <Form.Field>
                            <label>Stamp End &nbsp;</label>
                            <Input
                                size="small"
                                type="number"
                                placeholder="To"
                                value={editingStamp.to}
                                className="hide-number-arrows"
                                data-cy="stamps_to"
                                icon={{
                                    name: "close",
                                    link: true,
                                    onClick: e => {
                                        e.preventDefault();
                                        handleStampChange({ to: "" });
                                    },
                                }}
                                onChange={(e, { value }) => {
                                    const next = { ...editingStamp, to: value };
                                    handleStampChange(next);
                                    validateStampEnd(next);
                                    checkOverlap(next);
                                }}
                            />
                            {!!stampEndError && (
                                <div
                                    data-cy="stamp_end_error"
                                    className="ui pointing above prompt label"
                                >
                                    {stampEndError}
                                </div>
                            )}
                        </Form.Field>
                    </Form.Group>
                    {rollExists && (
                        <Message
                            data-cy="stamp_roll_info"
                            info
                            size="small"
                            className="no-print compact"
                        >
                            Stamp roll:{" "}
                            <strong>
                                {formatStampRange({
                                    from: editingStamp.roll_from,
                                    to: editingStamp.roll_to,
                                    prefix: editingStamp.prefix || "",
                                })}
                            </strong>
                        </Message>
                    )}
                </Form>
            </Segment>
            <Button.Group size="tiny" attached>
                <Button
                    content="Add"
                    icon="checkmark"
                    onClick={handleStampRollAdd}
                    disabled={
                        !editingStamp.roll_id ||
                        !!stampStartError ||
                        !!stampEndError ||
                        !!isResolving
                    }
                    data-cy="add_stamp_range_button"
                />
                <Button
                    content="Cancel"
                    onClick={handleInputClear}
                    icon="close"
                    data-cy="cancel_stamp_range_button"
                />
            </Button.Group>
        </>
    );
};

export default InvoiceBuilderContainerStampInput;
